import React, { useState, useEffect, useCallback } from 'react'
import { useRelayEnvironment } from 'react-relay/hooks'
import { AuthContext } from 'contexts'
import useApi from 'hooks/data/useApi'
import fetchCurrentUser from 'queries/fetchCurrentUser'
import { userIsOrgAdmin } from 'utils/user'
import { calculateTrialDaysRemaining } from '../utils/trial'

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [currentCompany, setCurrentCompany] = useState(null)
  const [currentOrg, setCurrentOrg] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const [freeTrial, setFreeTrial] = useState(false)
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0)
  const environment = useRelayEnvironment()
  const api = useApi()

  const login = useCallback(async () => {
    const currentUser = await fetchCurrentUser(environment)

    setUser(currentUser)
    setIsReady(true)
  }, [environment])

  const logout = useCallback(async () => {
    await api.auth.logout()
    setUser(null)
  }, [api, setUser])

  const handleSetCurrentOrg = useCallback(
    (org) => {
      if (currentOrg?.id !== org?.id) {
        setCurrentCompany(null)
        setCurrentOrg(org)

        const trialDays = calculateTrialDaysRemaining(org.createdAt)
        setFreeTrial(trialDays > 0)
        setTrialDaysRemaining(trialDays)
      }
    },
    [setCurrentCompany, setCurrentOrg, currentOrg],
  )

  useEffect(() => {
    login()
  }, [login])

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn: !!user,
        isReady,
        login,
        logout,
        freeTrial,
        trialDaysRemaining,
        currentCompany,
        setCurrentCompany,
        currentOrg,
        setCurrentOrg: handleSetCurrentOrg,
        userIsCurrentOrgAdmin: userIsOrgAdmin(user, currentOrg),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
